/* eslint-disable @typescript-eslint/no-unused-vars */
import { Fragment, MouseEvent, useState } from "react";

import { Button, Grid2, Typography } from "@mui/material";
import classNames from "classnames";
import moment, { Moment } from "moment-timezone";
import Calendar from "react-calendar";
import { Value } from "react-calendar/dist/cjs/shared/types";
import {
	FieldPath,
	FieldPathValue,
	SetValueConfig,
	useForm,
	useWatch,
} from "react-hook-form";
import { toast } from "react-toastify";

import { ECourseFrequency, ETimeBreak } from "../../../../../@types";
import { apis, callApi } from "../../../../../api";
import { defaultTimezone } from "../../../../../constants/timezone";
import { USDFormatter, formatTwoDigit, omitEmpty } from "../../../../../utils";
import { eventNames, logEvent } from "../../../../../utils/logEvent";
import { DatePicker } from "../../../../common/DatePicker/DatePicker";
import { ExplanationPopover } from "../../../../common/ExplanationPopover/ExplanationPopover";
import { TimePicker } from "../../../../common/TimePicker/TimePicker";
import {
	useBookingCourse,
	useCheckoutCourse,
} from "../hooks/useCheckoutCourse";

type TProposedScheduler = {
	date: Moment;
	time: string;
	options: number[];
};

type TOnsiteCourseScheduleFieldValues = {
	proposedScheduler: TProposedScheduler[];
};

export function OnsiteCourseCheckout() {
	const { course } = useCheckoutCourse();
	const { availableHours, bookings, policy, busyTimes } = useBookingCourse();

	type TState = {
		selectedDate: Date | null;
	};

	const [state, setState] = useState<TState>({
		selectedDate: null,
	});

	const { control, setValue, handleSubmit } =
		useForm<TOnsiteCourseScheduleFieldValues>({
			defaultValues: {
				proposedScheduler: [],
			},
			mode: "all",
		});
	const { proposedScheduler } = useWatch({
		control,
	}) as TOnsiteCourseScheduleFieldValues;

	const calculateTotalCost = () => {
		if (!course) {
			return 0;
		}
		let totalCost = 0;

		for (let i = 0; i < course.lesson.length; i++) {
			totalCost += course.lesson[i].totalCost ?? 0;
		}

		return totalCost;
	};

	const totalPrice = calculateTotalCost();

	const serviceFee = Math.round((totalPrice * 0.1 + 0.3) * 100) / 100;
	const totalCost = Math.round((totalPrice + serviceFee) * 100) / 100;

	const getAvailableTimeSlots = (
		date: Date | null,
		minutes: number,
		startMinutes?: number,
	): number[] => {
		if (!course || !date || !policy || course.lesson.length === 0) {
			return [];
		}

		const timeBreak = policy.timeBreak;

		const dayOfWeek = date.getDay();

		const availableHoursOfDay = availableHours.filter(
			(availableHour) => availableHour.dayOfWeek === dayOfWeek,
		);

		if (availableHoursOfDay.length === 0) {
			return [];
		}

		const minuteArray = Array.from({ length: 24 * 60 + 1 }).fill(
			false,
		) as boolean[];

		for (let i = 0; i < availableHoursOfDay.length; i++) {
			const startInMinute =
				availableHoursOfDay[i].startHour * 60 +
				availableHoursOfDay[i].startMinute;
			let endInMinute =
				availableHoursOfDay[i].endHour * 60 + availableHoursOfDay[i].endMinute;
			if (endInMinute === 0) {
				endInMinute = 24 * 60;
			}

			if (startInMinute === 0) {
				minuteArray[0] = true;
			}
			for (let j = startInMinute + 1; j <= endInMinute + 1 - minutes; j++) {
				minuteArray[j] = true;
			}

			if (startMinutes !== undefined) {
				for (let i = 0; i <= startMinutes; i++) {
					minuteArray[i] = false;
				}
			}
		}

		[...bookings, ...busyTimes.map((time) => ({ ...time, lessonId: 0 }))]
			.filter((bookingItem) => {
				const lessonStartTime = new Date(
					moment(bookingItem.startTime)
						.tz(course?.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);
				const lessonEndTime = new Date(
					moment(bookingItem.endTime)
						.tz(course?.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);

				if (
					lessonEndTime.getTime() <= new Date(date.toDateString()).getTime()
				) {
					return false;
				}
				if (
					lessonStartTime.getTime() >=
					new Date(date.toDateString()).getTime() + 86400000
				) {
					return false;
				}
				return true;
			})
			.forEach((bookingItem) => {
				const lessonStartTime = new Date(
					moment(bookingItem.startTime)
						.tz(course.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);
				let startTimeToMinute =
					lessonStartTime.getHours() * 60 + lessonStartTime.getMinutes();
				if (lessonStartTime.toDateString() !== date.toDateString()) {
					startTimeToMinute = 0;
				}
				startTimeToMinute = Math.max(
					0,
					startTimeToMinute - timeBreak - minutes,
				);

				const lessonEndTime = new Date(
					moment(bookingItem.endTime)
						.tz(course.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);
				let endTimeToMinute =
					lessonEndTime.getHours() * 60 + lessonEndTime.getMinutes();
				if (lessonEndTime.toDateString() !== date.toDateString()) {
					endTimeToMinute = 24 * 60;
				}
				endTimeToMinute = Math.min(24 * 60, endTimeToMinute + timeBreak);

				if (startTimeToMinute === 0) {
					minuteArray[0] = false;
				}

				for (let i = startTimeToMinute + 1; i <= endTimeToMinute; i++) {
					minuteArray[i] = false;
				}
			});

		const timeRanges = [];
		let start = null;
		for (let i = 0; i < minuteArray.length; i++) {
			if (minuteArray[i] && start === null) {
				if (i === 0) {
					start = i;
				} else {
					start = i - 1;
				}
			} else if (!minuteArray[i] && start !== null) {
				timeRanges.push({
					startTime: start,
					endTime: i - 1,
				});
				start = null;
			}
		}
		if (start !== null) {
			timeRanges.push({
				startTime: start,
				endTime: 24 * 60,
			});
		}

		const result = [];

		for (let i = 0; i < timeRanges.length; i++) {
			for (
				let j = Math.ceil(timeRanges[i].startTime / 15);
				j <= Math.floor(timeRanges[i].endTime / 15);
				j++
			) {
				result.push(j * 15);
			}
		}

		return result;
	};

	const calculateIsCheckoutButtonDisabled = () => {
		if (!course || course.lesson.length === 0) {
			return true;
		}

		return false;
	};

	const isCheckoutButtonDisabled = calculateIsCheckoutButtonDisabled();

	const onClickCheckout = async (data: TOnsiteCourseScheduleFieldValues) => {
		if (!course) {
			return;
		}

		const body = {
			productCode: course.courseCode,
			schedules: data.proposedScheduler.map((item, index) => ({
				lessonId: course.lesson[index].id,
				lessonDate: moment
					.tz(
						`${item.date.format("YYYY-MM-DD")} ${formatTwoDigit(Number(item.time.split(":")[0]))}:${formatTwoDigit(Number(item.time.split(":")[1]))}`,
						course.timezoneName ?? defaultTimezone,
					)
					.toISOString(),
			})),
			totalCost: Math.round(totalCost * 100),
		};

		try {
			const response = await callApi({
				method: "POST",
				url: apis.CHECKOUT_BOOKING,
				body: omitEmpty(body),
			});
			if (!response.paymentResult) {
				return;
			}
			logEvent(eventNames.purchaseOnsiteCourseFull, {
				courseId: course.id,
			});
			window.location.href = response.paymentResult.url;
		} catch (error: any) {
			if (error.response) {
				if (error.response.status === 400 || error.response.status === 404) {
					toast.error(error.response.data?.message);
				}
			}
		}
	};

	const proposeScheduler = (
		date: Date,
		index: number = 0,
		result: TProposedScheduler[] = [],
	): boolean => {
		if (!course || !policy) {
			return false;
		}
		if (course.frequency === ECourseFrequency.DAILY) {
			const classesPerDay = course.classesPerDay;
			const lessons = course.lesson.slice(index, index + classesPerDay);
			const totalTime =
				lessons.reduce((cur, acc) => cur + (acc.blockTime ?? 0), 0) +
				policy.timeBreak * (lessons.length - 1);
			let dayNeedToCheck = 90;
			if (index > 0) {
				dayNeedToCheck = 1;
			}
			for (let i = 0; i < dayNeedToCheck; i++) {
				const checkingDate = moment(date).add(i, "day");
				const availableTimeSlots = getAvailableTimeSlots(
					moment(checkingDate).toDate(),
					totalTime,
				);
				if (availableTimeSlots.length > 0) {
					let totalTime = 0;
					for (let j = 0; j < lessons.length; j++) {
						const appointmentTime = availableTimeSlots[0] + totalTime;
						result.push({
							date: moment(checkingDate),
							time: `${formatTwoDigit(Math.floor(appointmentTime / 60))}:${formatTwoDigit(appointmentTime % 60)}`,
							options: getAvailableTimeSlots(
								moment(checkingDate).toDate(),
								lessons[j].blockTime ?? 0,
								appointmentTime,
							),
						});
						totalTime += (lessons[j].blockTime ?? 0) + policy.timeBreak;
					}
					if (index + lessons.length == course.lesson.length) {
						return true;
					} else {
						const res = proposeScheduler(
							moment(checkingDate).add(1, "day").toDate(),
							index + lessons.length,
							result,
						);

						if (res) {
							return true;
						}
					}

					for (let j = 0; j < lessons.length; j++) {
						result.pop();
					}
				}
			}

			return false;
		} else if (course.frequency === ECourseFrequency.WEEKLY) {
			let dayLeftInWeek = 0;
			if (date.getDay() === 1) {
				dayLeftInWeek = 7;
			} else {
				dayLeftInWeek = (8 - date.getDay()) % 7;
			}

			const classesPerDay = course.classesPerDay;
			let weekNeedToCheck = 13;
			if (index > 0) {
				weekNeedToCheck = 2;
			}
			for (let i = 0; i < weekNeedToCheck; i++) {
				const res: TProposedScheduler[] = [];
				let checkedLesson = 0;
				for (
					let j = 0;
					j < dayLeftInWeek && checkedLesson < classesPerDay;
					j++
				) {
					const lesson = course.lesson[checkedLesson + index];
					const checkingDate = moment(date).add(i, "week").add(j, "day");
					const availableTimeSlots = getAvailableTimeSlots(
						checkingDate.toDate(),
						lesson.blockTime ?? 0,
					);
					if (availableTimeSlots.length > 0) {
						const appointmentTime = availableTimeSlots[0];
						checkedLesson++;
						res.push({
							date: checkingDate,
							time: `${formatTwoDigit(Math.floor(appointmentTime / 60))}:${formatTwoDigit(appointmentTime % 60)}`,
							options: availableTimeSlots,
						});

						if (res.length + index === course.lesson.length) {
							result.push(...res);
							return true;
						}
					}
				}

				if (res.length === classesPerDay) {
					result.push(...res);

					// If date is Monday
					let nextDate = moment(date)
						.add(i + 1, "week")
						.toDate();
					if (date.getDate() !== 1) {
						nextDate = moment(date).add(dayLeftInWeek, "day").toDate();
					}

					const res2 = proposeScheduler(nextDate, index + res.length, result);

					if (res2) {
						return true;
					}

					for (let j = 0; j < res.length; j++) {
						result.pop();
					}
				}
			}

			return false;
		}

		return false;
	};

	const onSelectDate = (value: Value, event: MouseEvent<HTMLButtonElement>) => {
		if (
			value instanceof Date &&
			value.toDateString() !== state.selectedDate?.toDateString()
		) {
			const result: TProposedScheduler[] = [];
			proposeScheduler(value, 0, result);
			setValue("proposedScheduler", result);
			if (result.length === 0) {
				toast.error(
					`We couldn't fit you between ${moment(value).format("DD MMMM YYYY")} and ${moment(value).add(90, "day").format("DD MMMM YYYY")}. Select your preferred start date beyond ${moment(value).add(90, "day").format("DD MMMM YYYY")}.`,
				);
			}
			setState((prevState) => ({
				...prevState,
				selectedDate: value,
			}));
		}
	};

	const renderSelectFirstLessonTime = () => {
		if (course?.lesson.length === 0) {
			return null;
		}

		if (!course) {
			return null;
		}

		return (
			<>
				<Typography className="small-title-text checkout-title-text">
					1. Select Preferred Start Date
				</Typography>
				<Calendar
					onChange={onSelectDate}
					value={state.selectedDate}
					minDate={new Date(new Date().getTime() + 86400000)}
					className="calendar-container normal-text"
					locale="en-US"
				/>
				<Typography className="normal-text bold timezone-text">
					{`${course.timezoneName ?? defaultTimezone} time`}
				</Typography>
			</>
		);
	};

	const renderSummary = () => {
		if (
			!course ||
			course.lesson.length === 0 ||
			proposedScheduler.length === 0
		) {
			return null;
		}

		let frequency = "";
		if (course.frequency === ECourseFrequency.DAILY) {
			frequency = "day";
		} else if (course.frequency === ECourseFrequency.WEEKLY) {
			frequency = "week";
		}

		return (
			<>
				<Typography
					marginTop="16px"
					className="small-title-text checkout-title-text">
					2. Review & Finish
				</Typography>
				<Typography className="normal-text" textAlign="center">
					Tada! Here is a proposed schedule based on the earliest
				</Typography>
				<Typography className="normal-text" textAlign="center">
					availability that allows you to stick to the recommended
				</Typography>
				<Typography className="normal-text" textAlign="center">
					{`pace of ${course.classesPerDay} Classes every ${frequency} until completion.`}
				</Typography>
				<Typography className="normal-text" textAlign="center">
					Feel free to adjust dates and times.
				</Typography>
				<Grid2 container spacing={1}>
					<Grid2 size={{ xs: 12, sm: 7, md: 7, lg: 7, xl: 7 }}>
						<Typography className="big-text bold">Class</Typography>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 3, md: 3, lg: 3, xl: 3 }}>
						<Typography className="big-text bold">Date</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 2, md: 2, lg: 2, xl: 2 }}>
						<Typography className="big-text bold">Time</Typography>
					</Grid2>
					{course.lesson.map((lesson, index) => {
						return (
							<Fragment key={lesson.id}>
								<Grid2 size={{ xs: 12, sm: 7, md: 7, lg: 7, xl: 7 }}>
									<div
										style={{
											height: "100%",
											display: "flex",
											alignItems: "center",
										}}>
										<Typography className="normal-text">
											{`Class ${index + 1} - ${lesson.title}`}
										</Typography>
									</div>
								</Grid2>
								<Grid2 size={{ xs: 8, sm: 3, md: 3, lg: 3, xl: 3 }}>
									<div
										style={{
											height: "100%",
											display: "flex",
											alignItems: "center",
										}}>
										<DatePicker
											control={control}
											minDate={
												proposedScheduler[index - 1]?.date ??
												moment(new Date(new Date().getTime() + 86400000))
											}
											maxDate={
												proposedScheduler[index + 1]?.date &&
												moment(proposedScheduler[index + 1].date).add(-1, "day")
											}
											key={
												proposedScheduler[index]?.date
													.toDate()
													.toDateString() ?? Math.random().toString()
											}
											name={`proposedScheduler.${index}.date`}
											setFormValue={(
												name: FieldPath<TOnsiteCourseScheduleFieldValues>,
												value: FieldPathValue<
													TOnsiteCourseScheduleFieldValues,
													FieldPath<TOnsiteCourseScheduleFieldValues>
												>,
												options?: SetValueConfig,
											) => {
												const thisDate = value as Moment;
												let startMinutes = 0;
												if (index > 0) {
													const prevLessonDate =
														proposedScheduler[index - 1].date;
													if (prevLessonDate.isSame(thisDate, "day")) {
														startMinutes =
															proposedScheduler[index - 1].options[0] +
															(course.lesson[index - 1].blockTime ?? 0) +
															(policy?.timeBreak ?? ETimeBreak.FIFTEEN_MINUTES);
													}
												}
												setValue(`proposedScheduler.${index}.time`, "");
												setValue(
													`proposedScheduler.${index}.options`,
													getAvailableTimeSlots(
														thisDate.toDate(),
														lesson.blockTime ?? 0,
														startMinutes,
													),
												);
												setValue(name, value, options);
											}}
										/>
									</div>
								</Grid2>
								<Grid2 size={{ xs: 4, sm: 2, md: 2, lg: 2, xl: 2 }}>
									<div
										style={{
											height: "100%",
											display: "flex",
											alignItems: "center",
										}}>
										<TimePicker
											control={control}
											key={proposedScheduler[index].time}
											name={`proposedScheduler.${index}.time`}
											availableTimes={proposedScheduler[index]?.options.map(
												(option) => ({
													id: option,
													label: `${formatTwoDigit(Math.floor(option / 60))}:${formatTwoDigit(option % 60)}`,
													value: `${formatTwoDigit(Math.floor(option / 60))}:${formatTwoDigit(option % 60)}`,
												}),
											)}
											rules={{
												required: {
													value: true,
													message: "Time is required.",
												},
											}}
										/>
									</div>
								</Grid2>
							</Fragment>
						);
					})}
					<Grid2 size={{ xs: 4, sm: 7, md: 7, lg: 7, xl: 7 }} />
					<Grid2 size={{ xs: 4, sm: 3, md: 3, lg: 3, xl: 3 }}>
						<Typography className="normal-text">Cost</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 2, md: 2, lg: 2, xl: 2 }}>
						<Typography className="normal-text money-text" textAlign="right">
							{USDFormatter.format(totalPrice)}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 7, md: 7, lg: 7, xl: 7 }} />
					<Grid2 size={{ xs: 4, sm: 3, md: 3, lg: 3, xl: 3 }}>
						<div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
							<Typography className="normal-text">Service fee</Typography>
							<ExplanationPopover text="All payments processed through the platform go 100% to instructors. We charge a service fee per transaction to build the ecosystem where the creative and good instructors can win." />
						</div>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 2, md: 2, lg: 2, xl: 2 }}>
						<Typography className="normal-text money-text" textAlign="right">
							{USDFormatter.format(serviceFee)}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 7, md: 7, lg: 7, xl: 7 }} />
					<Grid2 size={{ xs: 4, sm: 3, md: 3, lg: 3, xl: 3 }}>
						<Typography className="big-text bold">TOTAL DUE</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 2, md: 2, lg: 2, xl: 2 }}>
						<Typography className="big-text bold money-text" textAlign="right">
							{USDFormatter.format(totalCost)}
						</Typography>
					</Grid2>
				</Grid2>
				<div className="checkout-button-container">
					<Button
						className={classNames("normal-button checkout-button", {
							"primary-button": !isCheckoutButtonDisabled,
							"disabled-button": isCheckoutButtonDisabled,
						})}
						disabled={isCheckoutButtonDisabled}
						onClick={handleSubmit(onClickCheckout)}>
						Pay to Confirm
					</Button>
				</div>
			</>
		);
	};

	if (!course) {
		return null;
	}

	return (
		<div className="course-checkout-information-container">
			{renderSelectFirstLessonTime()}
			{state.selectedDate !== null && renderSummary()}
		</div>
	);
}
